import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { Suspense, lazy, useEffect } from "react";
import axios from "axios";
import { AuthProvider, useAuth } from "./context/AuthContext";
import HomePage from "./pages/HomePage";
import BecomePromoter from "./pages/BecomePromoter";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import MarketplacePage from "./pages/MarketplacePage";
import PageLayout from "./components/PageLayout";
import SiteAccessGate from "./components/SiteAccessGate";
import "./App.css";
import "./theme.css";

// Lazy load heavy components for better PageSpeed
const VideoMarketplacePage = lazy(() => import("./pages/VideoMarketplacePage"));
const VideoServiceDetailPage = lazy(() => import("./pages/VideoServiceDetailPage"));
const GigDetailPage = lazy(() => import("./pages/GigDetailPage"));
const PromoterDashboard = lazy(() => import("./pages/dashboards/PromoterDashboard"));
const ArtistDashboard = lazy(() => import("./pages/dashboards/ArtistDashboard"));
const LabelDashboard = lazy(() => import("./pages/dashboards/LabelDashboard"));
const AdminDashboard = lazy(() => import("./pages/dashboards/AdminDashboard"));
const VideoEditorDashboard = lazy(() => import("./pages/dashboards/VideoEditorDashboard"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("./pages/PaymentCancelPage"));
const PlaylistsPage = lazy(() => import("./pages/PlaylistsPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const GoogleAuthCallback = lazy(() => import("./pages/GoogleAuthCallback"));
const BriefsPage = lazy(() => import("./pages/BriefsPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const ArtistCampaignsPage = lazy(() => import("./pages/ArtistCampaignsPage"));
const PromoterCampaignsPage = lazy(() => import("./pages/PromoterCampaignsPage"));
const EditorPortfolioPage = lazy(() => import("./pages/EditorPortfolioPage"));
const PublicProfilePage = lazy(() => import("./pages/PublicProfilePage"));
const ArtistPublicProfile = lazy(() => import("./pages/ArtistPublicProfile"));
const PromoterPublicProfile = lazy(() => import("./pages/PromoterPublicProfile"));
const OrderDetailPage = lazy(() => import("./pages/OrderDetailPage"));
const ArtistDiscoveryPage = lazy(() => import("./pages/ArtistDiscoveryPage"));
const DistributionPage = lazy(() => import("./pages/DistributionPage"));
const DistributionPlans = lazy(() => import("./pages/DistributionPlans"));
const DistributionPlanSuccess = lazy(() => import("./pages/DistributionPlanSuccess"));
const CopyrightComplaintPage = lazy(() => import("./pages/CopyrightComplaintPage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const DistributionAgreementPage = lazy(() => import("./pages/DistributionAgreementPage"));
const CookieBanner = lazy(() => import("./components/CookieBanner"));
const PlatformRulesPage = lazy(() => import("./pages/PlatformRulesPage"));
const ReportAbusePage = lazy(() => import("./pages/ReportAbusePage"));
const ReleaseTeaserPage = lazy(() => import("./pages/ReleaseTeaserPage"));

const DistributionComingSoon = lazy(() => import("./pages/DistributionComingSoon"));

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
    <div className="spinner"></div>
  </div>
);

const DASHBOARD_PREVIEW_ROLES = [
  { id: "admin", label: "Administrator", path: "admin" },
  { id: "artist", label: "Artist", path: "artist" },
  { id: "label", label: "Record Label", path: "label" },
  { id: "promoter", label: "Promoter", path: "promoter" },
  { id: "video_editor", label: "Visual Artist", path: "editor" },
];

const AdminDashboardPreview = ({ role, children }) => {
  const navigate = useNavigate();
  const selectedRole = DASHBOARD_PREVIEW_ROLES.find((item) => item.id === role);

  const switchRole = (nextRole) => {
    const target = DASHBOARD_PREVIEW_ROLES.find((item) => item.id === nextRole);
    if (!target) return;
    if (target.id === "admin") {
      sessionStorage.removeItem("adminDashboardPreviewRole");
      navigate("/dashboard/admin");
      return;
    }
    sessionStorage.setItem("adminDashboardPreviewRole", target.id);
    navigate(`/dashboard/${target.path}?preview=${target.id}`);
  };

  return (
    <>
      <div className="sticky top-0 z-[100] flex flex-wrap items-center justify-between gap-3 border-b border-[#3a4b00] bg-[#CCFF00] px-4 py-2 text-sm text-black" data-testid="admin-preview-switcher">
        <span className="font-semibold">Persistent admin sandbox · payments, email, OAuth, and uploads disabled</span>
        <label className="flex items-center gap-2 font-medium">
          <span>Dashboard</span>
          <select
            value={role}
            onChange={(event) => switchRole(event.target.value)}
            className="h-9 border border-black/20 bg-white px-2 text-sm text-black"
            aria-label="Preview dashboard role"
          >
            {DASHBOARD_PREVIEW_ROLES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
          </select>
        </label>
        <span className="sr-only">Currently previewing {selectedRole?.label || "dashboard"}</span>
      </div>
      {children}
    </>
  );
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const queryPreviewRole = new URLSearchParams(location.search).get("preview");
  const storedPreviewRole = user?.role === "admin" ? sessionStorage.getItem("adminDashboardPreviewRole") : null;
  const previewRole = queryPreviewRole || storedPreviewRole;
  const adminPreviewAllowed = user?.role === "admin" && allowedRoles?.length === 1 && previewRole === allowedRoles[0];

  useEffect(() => {
    if (queryPreviewRole && adminPreviewAllowed) {
      sessionStorage.setItem("adminDashboardPreviewRole", queryPreviewRole);
    }
  }, [queryPreviewRole, adminPreviewAllowed]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminPreviewAllowed && !queryPreviewRole) {
    const query = new URLSearchParams(location.search);
    query.set("preview", previewRole);
    return <Navigate to={`${location.pathname}?${query.toString()}${location.hash}`} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role) && !adminPreviewAllowed) {
    return <Navigate to="/dashboard" replace />;
  }

  return adminPreviewAllowed ? <AdminDashboardPreview role={previewRole}>{children}</AdminDashboardPreview> : children;
};

// Distribution is available to all users (guests and authenticated)

const DashboardRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  switch (user.role) {
    case "promoter":
      return <Navigate to="/dashboard/promoter" replace />;
    case "artist":
      return <Navigate to="/dashboard/artist" replace />;
    case "label":
      return <Navigate to="/dashboard/label" replace />;
    case "admin":
      return <Navigate to="/dashboard/admin" replace />;
    case "video_editor":
      return <Navigate to="/dashboard/editor" replace />;
    default:
      return <Navigate to="/" replace />;
  }
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/release-preview/:releaseId" element={<ReleaseTeaserPageRoute />} />
        <Route element={<PageLayout><Outlet /></PageLayout>}>
          <Route path="/" element={<HomePage />} />
          <Route path="/become-a-promoter" element={<BecomePromoter />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookies" element={<CookiePolicyPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/prijzen" element={<PricingPage />} />
          <Route path="/distributieovereenkomst" element={<DistributionAgreementPage />} />
          <Route path="/platform-rules" element={<PlatformRulesPage />} />
          <Route path="/report-abuse" element={<ReportAbusePage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/video-marketplace" element={<VideoMarketplacePage />} />
          <Route path="/video-service/:id" element={<VideoServiceDetailPage />} />
          <Route path="/editor/:id/portfolio" element={<EditorPortfolioPage />} />
          <Route path="/artist/:artistId/profile" element={<ArtistPublicProfile />} />
          <Route path="/promoter/:id" element={<PromoterPublicProfile />} />
          <Route
            path="/order/:id"
            element={
              <ProtectedRoute>
                <OrderDetailPage />
              </ProtectedRoute>
            }
          />
          <Route path="/artist-discovery" element={<ArtistDiscoveryPage />} />
          <Route path="/u/:id" element={<PublicProfilePage />} />
          <Route path="/open-campaigns" element={<PromoterCampaignsPage />} />
          <Route path="/briefs" element={<BriefsPage />} />
          <Route path="/playlists" element={<PlaylistsPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/gig/:id" element={<GigDetailPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/chat/g/:groupId" element={<ChatPage />} />
          <Route path="/chat/:userId" element={<ChatPage />} />
          <Route path="/dashboard" element={<DashboardRedirect />} />
        </Route>
        <Route
          path="/dashboard/promoter/*"
          element={
            <ProtectedRoute allowedRoles={["promoter"]}>
              <PromoterDashboard />
            </ProtectedRoute>
          }
        />
      <Route
        path="/dashboard/artist/*"
        element={
          <ProtectedRoute allowedRoles={["artist"]}>
            <ArtistDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/label/*"
        element={
          <ProtectedRoute allowedRoles={["label"]}>
            <LabelDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/admin/*"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/distribution" element={<PageLayout><ProtectedRoute><DistributionPage /></ProtectedRoute></PageLayout>} />
      <Route path="/distribution-plans" element={<PageLayout><DistributionPlans /></PageLayout>} />
      <Route path="/distribution/plan-success" element={<DistributionPlanSuccess />} />
          <Route path="/copyright-complaint" element={<CopyrightComplaintPage />} />
      <Route
        path="/dashboard/editor/*"
        element={
          <ProtectedRoute allowedRoles={["video_editor"]}>
            <VideoEditorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <PageLayout>
            <ProtectedRoute>
              <PaymentSuccessPage />
            </ProtectedRoute>
          </PageLayout>
        }
      />
      <Route
        path="/payment/cancel"
        element={
          <PageLayout>
            <ProtectedRoute>
              <PaymentCancelPage />
            </ProtectedRoute>
          </PageLayout>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function ReleaseTeaserPageRoute() {
  const { releaseId } = useParams();
  return <ReleaseTeaserPage releaseId={releaseId} />;
}

function App() {
  axios.defaults.withCredentials = true;

  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteAccessGate>
          <Toaster position="top-right" richColors />
          <CookieBanner />
          <AppRoutes />
        </SiteAccessGate>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
